import {
  Box,
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack
} from "@chakra-ui/react";

import { PiWarningCircleLight } from "react-icons/pi";

import { generateTestId } from "@/util/testIds";

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  content?: string;
  isLoading?: boolean;
  confirmText?: string;
  loadingText?: string;
  confirmAction?: () => void;
  showCancel?: boolean;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  title,
  content,
  isLoading,
  confirmText,
  loadingText,
  confirmAction,
  showCancel = true
}) => (
  <Modal
    size="2xl"
    closeOnOverlayClick={!isLoading}
    closeOnEsc={!isLoading}
    isOpen={isOpen}
    onClose={onClose}
    isCentered
    data-testid={generateTestId.modal("confirm")}
  >
    <ModalOverlay data-testid={generateTestId.modal("confirm-overlay")} />
    <ModalContent
      py={6}
      minH={150}
      data-testid={generateTestId.modal("confirm-content")}
    >
      <ModalHeader
        pb={0}
        fontWeight="600"
        color="primary.500"
        textAlign="center"
        data-testid={generateTestId.modal("confirm-header")}
      >
        <VStack spacing={0}>
          <Icon
            fontSize={48}
            as={PiWarningCircleLight}
            data-testid={generateTestId.icon("warning")}
          />
          <Box
            pt={3}
            fontSize="2xl"
            as="span"
            data-testid={generateTestId.modal("confirm-title")}
          >
            {title}
          </Box>
        </VStack>
      </ModalHeader>
      <ModalBody
        py={3}
        as={HStack}
        justify="center"
        fontSize="sm"
        fontWeight={500}
        data-testid={generateTestId.modal("confirm-body")}
      >
        <Text
          textAlign="center"
          maxW={522}
          data-testid={generateTestId.modal("confirm-message")}
        >
          {content || "Are you sure?"}
        </Text>
      </ModalBody>

      <ModalFooter
        justifyContent="center"
        data-testid={generateTestId.modal("confirm-footer")}
      >
        {showCancel && (
          <Button
            isDisabled={isLoading}
            size="md"
            maxW={130}
            onClick={onClose}
            w="full"
            variant="outline"
            data-testid={generateTestId.button("cancel", "modal")}
          >
            Cancel
          </Button>
        )}
        <Button
          size="md"
          maxW={180}
          w="full"
          colorScheme="blue"
          ml={3}
          loadingText={loadingText || "Hold on..."}
          isLoading={isLoading}
          onClick={confirmAction}
          data-testid={generateTestId.button("confirm", "modal")}
        >
          {confirmText}
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);
